import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  TextField,
} from '@material-ui/core';
import CanvasRenderer from '../canvas/renderer/CanvasRenderer';
import { validateForPublish, validateCanvasDocument } from '../canvas/validate/validateCanvas';
import {
  kpiCanvas,
  kpiManifest,
  kpiDatasets,
} from '../canvas/fixtures/kpiDashboard';
import { intakeCanvas, intakeManifest } from '../canvas/fixtures/intakeForm';
import { isCanvasPlaygroundAllowed } from '../utils/canvasPlaygroundAccess';
import localPagesStore from '../canvas/api/localPagesStore';

const PRESETS = {
  kpi: {
    label: 'KPI dashboard',
    canvas: kpiCanvas,
    manifest: kpiManifest,
    datasets: kpiDatasets,
  },
  intake: {
    label: 'Customer intake form',
    canvas: intakeCanvas,
    manifest: intakeManifest,
    datasets: {},
  },
};

function pretty(obj) {
  return JSON.stringify(obj, null, 2);
}

/**
 * Lightweight internal Canvas Playground — paste/edit JSON, preview, inspect validation.
 * Not a visual editor.
 */
export default function CanvasPlayground() {
  const [preset, setPreset] = useState('kpi');
  const [canvasText, setCanvasText] = useState(pretty(PRESETS.kpi.canvas));
  const [manifestText, setManifestText] = useState(pretty(PRESETS.kpi.manifest));
  const [datasetsText, setDatasetsText] = useState(pretty(PRESETS.kpi.datasets));
  const [parseError, setParseError] = useState('');
  const [validation, setValidation] = useState({ ok: true, errors: [], warnings: [] });
  const [preview, setPreview] = useState({
    canvas: PRESETS.kpi.canvas,
    manifest: PRESETS.kpi.manifest,
    datasets: PRESETS.kpi.datasets,
  });
  const [previewKey, setPreviewKey] = useState(0);
  const [publishMsg, setPublishMsg] = useState('');
  const playgroundAllowed = isCanvasPlaygroundAllowed();

  const loadPreset = (key) => {
    const p = PRESETS[key];
    setPreset(key);
    setCanvasText(pretty(p.canvas));
    setManifestText(pretty(p.manifest));
    setDatasetsText(pretty(p.datasets));
    setPreview({ canvas: p.canvas, manifest: p.manifest, datasets: p.datasets });
    setPreviewKey((k) => k + 1);
    setParseError('');
    setValidation(validateForPublish(p.canvas, p.manifest));
  };

  const apply = () => {
    try {
      const canvas = JSON.parse(canvasText);
      const manifest = JSON.parse(manifestText);
      const datasets = JSON.parse(datasetsText);
      setParseError('');
      const result = validateForPublish(canvas, manifest);
      setValidation(result);
      setPreview({ canvas, manifest, datasets });
      // Remount renderer so prop edits (e.g. TextField variant) always paint fresh
      setPreviewKey((k) => k + 1);
    } catch (e) {
      setParseError(e.message || String(e));
    }
  };

  const publishLocal = () => {
    if (!localPagesStore.enabled()) {
      setPublishMsg('Local Pages store disabled in this environment.');
      return;
    }
    try {
      const canvas = JSON.parse(canvasText);
      const datasets = JSON.parse(datasetsText);
      const result = validateForPublish(canvas, JSON.parse(manifestText));
      setValidation(result);
      if (!result.ok) {
        setPublishMsg('Publish blocked — fix validation errors first.');
        return;
      }
      const pageId = canvas.page_id;
      const [tenant_id, slug] = String(pageId).split('/');
      try {
        localPagesStore.createPage({
          tenant_id,
          slug,
          title: canvas.title,
          visibility: canvas.visibility,
        });
      } catch (_) {
        /* already exists — update draft */
      }
      localPagesStore.updateDraft(pageId, canvas);
      const snapshots = (canvas.dataset_slots || []).map((slot) => {
        const ds = datasets[slot.dataset_id] || {};
        return {
          slot: slot.id,
          status: ds.status || 'validated',
          data: ds.data != null ? ds.data : ds,
        };
      });
      const pub = localPagesStore.publish(pageId, { initial_snapshots: snapshots });
      setPublishMsg(
        `Published locally as canvas v${pub.canvas_version}. Open /page/${pageId}`,
      );
    } catch (e) {
      setPublishMsg(e.message || String(e));
    }
  };

  const softValidate = useMemo(
    () => {
      try {
        return validateCanvasDocument(JSON.parse(canvasText), { forPublish: false });
      } catch {
        return null;
      }
    },
    [canvasText],
  );

  if (!playgroundAllowed) {
    return (
      <Box p={4}>
        <Typography variant="h6">Canvas Playground unavailable</Typography>
        <Typography color="textSecondary">
          This internal tool is disabled in production. Set REACT_APP_ENABLE_CANVAS_PLAYGROUND=true
          or localStorage workload_internal_dev=1 for internal developers.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2} style={{ background: '#F5F7FB', minHeight: '100vh' }}>
      <Typography variant="h5" gutterBottom>
        Workload Pages — Canvas Playground
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Internal development tool. Paste canvas / manifest / datasets JSON, validate, and preview.
      </Typography>
      <Box mb={2}>
        {Object.keys(PRESETS).map((key) => (
          <Button
            key={key}
            variant={preset === key ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            style={{ marginRight: 8 }}
            onClick={() => loadPreset(key)}
          >
            {PRESETS[key].label}
          </Button>
        ))}
        <Button variant="contained" color="secondary" size="small" onClick={apply}>
          Validate &amp; Preview
        </Button>
        {localPagesStore.enabled() ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            style={{ marginLeft: 8 }}
            onClick={publishLocal}
          >
            Publish locally
          </Button>
        ) : null}
        {publishMsg ? (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {publishMsg}
          </Typography>
        ) : null}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper style={{ padding: 12, marginBottom: 12 }}>
            <Typography variant="subtitle2">Canvas JSON</Typography>
            <TextField
              multiline
              fullWidth
              rows={16}
              value={canvasText}
              onChange={(e) => setCanvasText(e.target.value)}
              variant="outlined"
              margin="dense"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </Paper>
          <Paper style={{ padding: 12, marginBottom: 12 }}>
            <Typography variant="subtitle2">Manifest JSON</Typography>
            <TextField
              multiline
              fullWidth
              rows={8}
              value={manifestText}
              onChange={(e) => setManifestText(e.target.value)}
              variant="outlined"
              margin="dense"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </Paper>
          <Paper style={{ padding: 12 }}>
            <Typography variant="subtitle2">Datasets JSON</Typography>
            <TextField
              multiline
              fullWidth
              rows={10}
              value={datasetsText}
              onChange={(e) => setDatasetsText(e.target.value)}
              variant="outlined"
              margin="dense"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper style={{ padding: 12, marginBottom: 12 }}>
            <Typography variant="subtitle2">Validation</Typography>
            {parseError ? (
              <Typography color="error">Parse error: {parseError}</Typography>
            ) : null}
            <Typography
              style={{ color: validation.ok ? '#1e7e34' : '#c62828', fontWeight: 600 }}
            >
              Publish gate: {validation.ok ? 'PASS' : 'FAIL'}
            </Typography>
            {(validation.errors || []).map((e) => (
              <Typography key={e} variant="body2" color="error">
                • {e}
              </Typography>
            ))}
            {(validation.warnings || []).map((w) => (
              <Typography key={w} variant="body2" style={{ color: '#b26a00' }}>
                • {w}
              </Typography>
            ))}
            {softValidate && !softValidate.ok ? (
              <Box mt={1}>
                <Typography variant="caption">Draft (non-publish) issues:</Typography>
                {softValidate.errors.map((e) => (
                  <Typography key={e} variant="caption" display="block" color="textSecondary">
                    {e}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Paper>
          <Paper style={{ padding: 8, minHeight: 400 }}>
            <Typography variant="subtitle2" style={{ padding: 8 }}>
              Preview
            </Typography>
            <CanvasRenderer
              key={previewKey}
              canvas={preview.canvas}
              manifest={preview.manifest}
              datasets={preview.datasets}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
